import { ActionIcon, Anchor, Badge, Group, Table, Text, Tooltip, Stack } from '@mantine/core';
import { closeAllModals, openModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { IconCheck, IconPencil, IconTag } from '@tabler/icons-react';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotIdFAQsMethods } from '@mygpt/app';
import { MessageDetailInfo, MsgCitation, SessionInfo } from '@mygpt/types';
import type { QuestionTagGroupOut } from '@mygpt/types/tag';
import { useBot } from '@mygpt/app/bot';
import { cleanAgentContent } from '@mygpt/app/utils/agent';

import { FaqsItemEdit, FaqsItemEditProps } from '@/components/Faqs/item-edit';
import { RatingData, RatingItem } from '@/components/TeachModel/FeedbackTable';

import { TagSelectorModal } from '@/components/MessageHistory/TagSelectorModal';
import { useDisclosure } from '@mantine/hooks';
import { useGPTStore } from '@/store';
import { groupTagsByGroup } from './util';
import { formatPageHost } from '@/utils/pageUrl';
import cx from 'clsx';
import classes from './table.module.css';
import sticky from '@/styles/stickyTable.module.css';

interface UnansweredTableProps {
  data: Array<UnansweredTableItem>;
  tagGroups: QuestionTagGroupOut[];
  loadingTagGroups: boolean;
  onRefreshTagGroups: () => void;
  actionCallBack?: () => void;
}

export interface UnansweredTableItem {
  transfer_to_human: any;
  id: string;
  faqId: string | null;
  citation: MsgCitation;
  time: number;
  question: string;
  answer: string;
  rating: RatingData[];
  detail: string;
  session: SessionInfo;
  tags: MessageDetailInfo['tags'];
  /** 来源页面 URL */
  url: string;
}

export const FeedbackTable: React.FC<UnansweredTableProps> = ({
  actionCallBack,
  data,
  tagGroups,
  loadingTagGroups,
  onRefreshTagGroups,
}) => {
  const { t } = useTranslation();
  const { addFAQs, updateFAQs } = useNotIdFAQsMethods();
  const [opened, { open: openTagModal, close: closeTagModal }] = useDisclosure(false);
  const [selectedMessage, setSelectedMessage] = React.useState<UnansweredTableItem | null>(null);
  const { quotaInfo } = useGPTStore((state) => state);
  const currentPlan = quotaInfo?.active_plan_subscription?.plan;
  const isLocalMode = import.meta.env.VITE_MODE === 'local';
  const { robotInfo } = useBot();

  const TypeData = [
    { value: 'all', label: t('chat_history.source_type.all') },
    { value: 'playground', label: t('chat_history.source_type.playground') },
    { value: 'share', label: t('chat_history.source_type.share') },
    { value: 'widget', label: t('chat_history.source_type.widget') },
    { value: 'api', label: t('chat_history.source_type.api') },
    { value: 'realtime', label: t('chat_history.source_type.realtime') },
  ];

  const getSourceLabel = (source: string) => {
    return TypeData.find((_) => _.value === source)?.label;
  };

  const editFaq = (item: UnansweredTableItem) => {
    const isEdit = !!item.faqId;
    const onSubmitEditFaq = async (params: Parameters<NonNullable<FaqsItemEditProps['onSubmit']>>[0]) => {
      const datasetId = params.datasetId || '';

      const successTip = async () => {
        showNotification({
          message: t('save_success'),
          color: 'green.5',
        });

        // 如果是新增就重新刷新数据获取faqId
        if (!item.faqId) {
          actionCallBack && (await actionCallBack());
        }
      };

      if (item.faqId) {
        await updateFAQs({ datasetId, faqsId: item.faqId || '', ...params }).then(() => {
          successTip();
        });
      } else {
        await addFAQs({ datasetId, ...params, session_message_id: item.id }).then((res) => {
          console.log(res);
          successTip();
        });
      }
      closeAllModals();
    };

    // 清理答案中的 agent 特殊标签（如 [TOOL_CALL]、[TOOL_RESPONSE] 等）
    const cleanedAnswer = cleanAgentContent(item.answer || '');

    const faqs = {
      id: 'id',
      created_at: new Date().toISOString(),
      user_id: item.id,
      question: item.question,
      answer: cleanedAnswer,
      rating: 0,
      sources: '',
      updated_at: '',
    };

    openModal({
      title: isEdit ? t('faqs.edit_title') : t('faqs.add_title'),
      withinPortal: false,
      centered: true,
      radius: 'md',
      size: 'lg',
      children: (
        <FaqsItemEdit
          selectDatasetId
          datasetId={''}
          faqs={faqs}
          faqId={item.faqId}
          citation={item.citation}
          onSubmit={onSubmitEditFaq}
          onSubmitText={isEdit ? t('save') : t('faqs.action.add')}
        />
      ),
    });
  };

  const generateTag = (item: UnansweredTableItem) => {
    setSelectedMessage(item);
    openTagModal();
  };

  // 检查标签是否在当前标签组中存在
  const isTagValid = (name: string) => {
    return tagGroups.some((group) => group.tags.some((tag) => tag.name === name));
  };

  // 标签分组展示组件 - 方案3：合并列展示
  const TagGroupDisplay: React.FC<{ tags: UnansweredTableItem['tags'] }> = ({ tags }) => {
    const groupedTags = groupTagsByGroup(tags);

    return (
      <Stack gap="xs">
        {Object.entries(groupedTags).map(([groupId, groupData]) => (
          <Group key={groupId} gap="xs" align="flex-start">
            <Badge variant="filled" size="md">
              {groupData.groupName}
            </Badge>
            <Group gap="xs">
              {groupData.tags.map((tag) => (
                <Badge key={tag.id} variant="light" size="md">
                  {tag.name}
                  {!isTagValid(tag.name) ? t('tag.tag_deleted') : ''}
                </Badge>
              ))}
            </Group>
          </Group>
        ))}
      </Stack>
    );
  };

  return (
    <div>
      {/* 列数多、日语表头长，容器内横向滚动，避免把页面撑出去；圆角放到滚动容器上 */}
      <Table.ScrollContainer minWidth={1200} className="rounded-lg">
        {/* 主题给 table 设了 overflow: hidden，会让 sticky 单元格相对 table 而不是滚动容器定位，这里放开 */}
        <Table styles={{ table: { overflow: 'visible' } }}>
          <Table.Thead className="bg-gray-300">
            <Table.Tr>
              <Table.Th className={cx(sticky.stickyCol, classes.stickyColQuestion)}>
                {t('teach_model.table.question')}
              </Table.Th>
              <Table.Th>{t('tag.tags')}</Table.Th>
              <Table.Th>{t('teach_model.table.rating')}</Table.Th>
              <Table.Th>{t('teach_model.table.feedback_details')}</Table.Th>
              <Table.Th>{t('chat_history.table.type')}</Table.Th>
              <Table.Th>{t('chat_history.table.source_url')}</Table.Th>
              <Table.Th>{t('chat_history.table.user')}</Table.Th>
              <Table.Th>{t('settings.feedback.has_feedback')}</Table.Th>
              <Table.Th className={cx(sticky.stickyCol, classes.stickyColTime)}>
                {t('chat_history.table.created_at')}
              </Table.Th>
              <Table.Th className={cx(sticky.stickyCol, classes.stickyColAction)}>
                {t('teach_model.table.action')}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.map((item) => (
              <Table.Tr key={item.id}>
                <Table.Td className={cx(sticky.stickyCol, classes.stickyColQuestion)}>
                  <Group gap={10} wrap="nowrap">
                    <span className="bg-gray-500 py-1 px-2 text-white text-sm rounded-md">Q</span>
                    <Tooltip disabled={!item.question} label={item.question} multiline maw={'50vw'}>
                      <Text size="sm" fw={500} lh={1.2} lineClamp={2}>
                        {item.question}
                      </Text>
                    </Tooltip>
                  </Group>
                </Table.Td>
                <Table.Td style={{ maxWidth: '30vw' }}>
                  {item.tags.length ? <TagGroupDisplay tags={item.tags} /> : '-'}
                </Table.Td>
                <Table.Td>
                  {item.rating.length
                    ? item.rating.map((item, index) => {
                        return <RatingItem key={index} data={item} />;
                      })
                    : '-'}
                </Table.Td>
                <Table.Td style={{ maxWidth: 240 }}>
                  <Tooltip
                    disabled={!item.detail}
                    label={<span style={{ wordWrap: 'break-word' }}>{item.detail}</span>}
                    multiline
                    maw={'50vw'}
                  >
                    <Text size="sm" truncate="end">
                      {item.detail || '-'}
                    </Text>
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" className="whitespace-nowrap">
                    {getSourceLabel(item.session.source) || item.session.source}
                  </Text>
                </Table.Td>
                <Table.Td style={{ maxWidth: 160 }}>
                  {item.url ? (
                    <Tooltip label={item.url} multiline maw={'50vw'}>
                      <Anchor href={item.url} target="_blank" rel="noreferrer" size="sm" truncate="end">
                        {formatPageHost(item.url)}
                      </Anchor>
                    </Tooltip>
                  ) : (
                    '-'
                  )}
                </Table.Td>
                <Table.Td style={{ maxWidth: 180 }}>
                  <Tooltip label={item.session.user_identifier} disabled={!item.session.user_identifier}>
                    <Text size="sm" truncate="end">
                      {item.session.user_identifier}
                    </Text>
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{item.transfer_to_human ? <IconCheck size={16} /> : ''}</Text>
                </Table.Td>
                <Table.Td className={cx(sticky.stickyCol, classes.stickyColTime)}>
                  <Text c="gray.4" size="sm" className="whitespace-nowrap">
                    {dayjs(item.time).format('YYYY-MM-DD HH:mm:ss')}
                  </Text>
                </Table.Td>
                <Table.Td className={cx(sticky.stickyCol, classes.stickyColAction)}>
                  <Group gap={5} wrap="nowrap">
                    <Tooltip label={t('tag.edit_tags')}>
                      <ActionIcon
                        variant="subtle"
                        onClick={() => generateTag(item)}
                        size="md"
                        disabled={!isLocalMode && !currentPlan?.enable_question_tag}
                      >
                        <IconTag size={18} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label={t('faqs.add_title')}>
                      <ActionIcon variant="subtle" onClick={() => editFaq(item)} size="md">
                        <IconPencil size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <TagSelectorModal
        opened={opened}
        onClose={() => {
          closeTagModal();
          setSelectedMessage(null);
        }}
        message={selectedMessage}
        tagGroups={tagGroups}
        loadingTagGroups={loadingTagGroups}
        onSuccess={() => {
          actionCallBack?.();
          onRefreshTagGroups();
          closeTagModal();
          setSelectedMessage(null);
        }}
      />
    </div>
  );
};
